import * as THREE from 'three';
import { SEED, clamp } from './util.js';
import { Occupancy, markWater, HeightGrid } from './layout.js';
import { createTerrain, createHeightTexture } from './terrain.js';
import { createRoads } from './roads.js';
import { createWater } from './water.js';
import { Sky } from './sky.js';
import { createPost } from './post.js';
import { createVillage } from './village.js';
import { createVegetation, windUniforms } from './vegetation.js';
import { NoteParticles, PuffSystem } from './effects.js';
import { Van } from './van.js';
import { Weasel } from './weasel.js';
import { CameraRig } from './camera.js';
import { Life } from './life.js';
import { ChipAudio, SONG_INFO } from './audio.js';
import { createUI, hourRate } from './ui.js';

const params = new URLSearchParams(location.search);
const canvas = document.getElementById('stage');
const nextFrame = () => new Promise((r) => requestAnimationFrame(() => r()));

async function boot() {
  // let the title card paint before the (synchronous) world build blocks the thread
  await nextFrame();
  await nextFrame();
  try {
    await Promise.all([document.fonts.load('700 64px Fredoka'), document.fonts.load('16px "Press Start 2P"')]);
  } catch (e) { /* fall back to system fonts */ }

  let renderer;
  try {
    renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
  } catch (err) {
    document.getElementById('loadNote').textContent = 'WebGL 2 is not available in this browser :(';
    document.getElementById('startLabel').textContent = 'NO WEBGL';
    console.error(err);
    return;
  }
  const lowPower = matchMedia('(pointer: coarse)').matches || (navigator.hardwareConcurrency || 8) <= 4;
  const maxPR = Math.min(window.devicePixelRatio || 1, lowPower ? 1.5 : 2);
  renderer.setPixelRatio(params.has('pr') ? Number(params.get('pr')) : maxPR);
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.NeutralToneMapping;
  renderer.toneMappingExposure = 1;

  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(46, 1, 0.5, 1400);
  camera.position.set(0, 60, 120);

  const sky = new Sky(scene, renderer, lowPower ? 2048 : 4096);
  if (params.has('hour')) sky.setHour(Number(params.get('hour')));
  if (params.has('nofog')) { sky.fogScale = 0; sky.clouds.visible = false; }

  // ---- world
  const occ = new Occupancy();
  markWater(occ);
  scene.add(createTerrain());
  const water = createWater(createHeightTexture());
  scene.add(water.mesh);
  const { group: roads, spokes } = createRoads(occ);
  scene.add(roads);
  const village = createVillage(occ, spokes);
  scene.add(village.group);
  const vegetation = createVegetation(occ, village);
  scene.add(vegetation.group);

  // ---- audio + effects + actors
  const audio = new ChipAudio();
  const notes = new NoteParticles(scene);
  const puffs = new PuffSystem(scene, 90, { color: '#ffffff', opacity: 0.9 });
  const van = new Van(scene, village.info.stops, notes, puffs);
  const rig = new CameraRig(camera, canvas);
  const tallGrid = new HeightGrid();
  for (const o of [...village.info.tall, ...vegetation.info.tall]) tallGrid.add(o.x, o.z, o.r, o.h);
  rig.setObstacles(tallGrid);
  rig.setMode('cinematic');
  const tmpV = new THREE.Vector3();

  const weaselEnv = {
    sfx: (n) => audio.sfx(n),
    puffs,
    kick: (a) => rig.kick(a),
    vanRelative(p) {
      const dx = p.x - van.pos.x, dz = p.z - van.pos.z;
      const s = Math.sin(van.yaw), c = Math.cos(van.yaw);
      return { x: dx * c - dz * s, z: dx * s + dz * c };
    },
    roofPoint(out) {
      van.root.updateMatrixWorld(true);
      return van.local(0, 3.3, -1.05, out);
    },
    _tf: { head: new THREE.Vector3(), axis: new THREE.Vector3() },
    _out: { head: null, axis: new THREE.Vector3(), angle: 0, floorY: 0 },
    topperFrame() {
      van.root.updateMatrixWorld(true);
      const tf = this._tf, out = this._out;
      van.topper.getWorldPosition(tf.axis);
      tf.head.set(tf.axis.x, tf.axis.y + 1.65, tf.axis.z);
      out.head = tf.head;
      out.axis.set(tf.axis.x, tf.head.y - 0.1, tf.axis.z);
      out.angle = -(van.yaw + van.topper.rotation.y) + 0.4;
      out.floorY = van.pos.y + 2.8;
      return out;
    },
  };
  const weasel = new Weasel(scene, van, weaselEnv);
  const life = new Life(scene, village, vegetation, van, weasel, audio, notes);

  van.onHonk = () => { audio.sfx('honk'); rig.kick(0.2); };
  van.onStop = () => { audio.sfx('jingle'); life.onVanStop(); };

  const post = createPost(renderer, scene, camera);
  function resize() {
    const w = window.innerWidth, h = window.innerHeight;
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    post.setSize(w, h, renderer.getPixelRatio());
  }
  window.addEventListener('resize', resize);
  resize();

  // ---- state + UI
  const state = { paused: false, autoTime: true };
  const focus = new THREE.Vector3();
  let time = 0;

  function takePhoto() {
    post.composer.render(0);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `sprinkle-hollow-${Date.now()}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    }, 'image/png');
  }
  const ui = createUI({ audio, rig, sky, van, weasel, state, takePhoto });

  // ---- simulation step
  let nightPing = 0;
  function step(dt, moveCamera = true) {
    time += dt;
    if (state.autoTime && ui.started && !params.has('hour')) sky.setHour(sky.hour + dt * hourRate(sky.hour));
    const beat = audio.started ? audio.beat : (time * SONG_INFO.bpm) / 60;
    sky.update(dt, time, camera, focus);
    water.update(time, sky);
    windUniforms.uTime.value = time;
    village.update(dt, time, sky);
    van.update(dt, time, beat, sky.night);
    weasel.update(dt, time, beat);
    life.update(dt, time, sky);
    notes.update(dt, time);
    puffs.update(dt, 0.3);
    if (moveCamera) {
      rig.update(dt, time, van, weasel);
      rig.focusPoint(focus, van);
    }
    nightPing += dt;
    if (nightPing > 1) { nightPing = 0; audio.setNight(sky.night); }
  }

  // ---- click / tap on the van (honk) or the weasel (startle)
  const ray = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  canvas.addEventListener('click', (e) => {
    if (!ui.started || rig.moved > 6 || state.paused) return;
    ndc.set((e.clientX / window.innerWidth) * 2 - 1, -(e.clientY / window.innerHeight) * 2 + 1);
    ray.setFromCamera(ndc, camera);
    const hits = ray.intersectObjects([van.root, weasel.root, weasel.headGroup], true);
    for (const h of hits) {
      let o = h.object;
      while (o) {
        if (o === van.root) { van.honk(); return; }
        if (o === weasel.root || o === weasel.headGroup) { weasel.poke(); rig.kick(0.3); return; }
        o = o.parent;
      }
    }
  });

  // ---- debug / test hooks
  if (params.has('autostart')) ui.begin();
  if (params.has('cam')) {
    const v = params.get('cam');
    if (['follow', 'cinematic', 'overview'].includes(v)) rig.setMode(v);
    else {
      const a = v.split(',').map(Number);
      rig.mode = 'manual';
      camera.position.set(a[0], a[1], a[2]);
      camera.lookAt(a[3], a[4], a[5]);
      if (a[6]) { camera.fov = a[6]; camera.updateProjectionMatrix(); }
      focus.set(a[3], a[4], a[5]);
    }
  }
  const dbgLook = params.get('look');
  const dbgOff = (params.get('off') || '6,2.5,8').split(',').map(Number);
  if (dbgLook) rig.mode = 'manual';
  const applyDbgLook = () => {
    if (!dbgLook) return;
    const t = dbgLook === 'weasel' ? weasel.head : van.pos;
    camera.position.set(t.x + dbgOff[0], t.y + dbgOff[1], t.z + dbgOff[2]);
    camera.lookAt(t.x, t.y + (params.has('ly') ? Number(params.get('ly')) : dbgLook === 'van' ? 1.6 : 0.3), t.z);
    focus.copy(t);
  };
  const ff = Number(params.get('t')) || 0;
  for (let i = 0; i < ff * 30; i++) step(1 / 30, rig.mode !== 'manual');

  // warm up shaders so the first real frame does not hitch
  try { renderer.compile(scene, camera); } catch (e) { /* ignore */ }
  post.composer.render(0);
  ui.ready();
  document.getElementById('loadNote').textContent = 'click to start · sound on ♪';

  // ---- adaptive resolution
  let avg = 1 / 60, since = 0, calm = 0, lastDown = -99;
  let pr = renderer.getPixelRatio();
  function adapt(dt) {
    if (params.has('pr')) return;
    avg = avg * 0.94 + dt * 0.06;
    since += dt;
    if (since < 1.2) return;
    since = 0;
    let next = pr;
    if (avg > 1 / 38 && pr > 0.6) { next = Math.max(0.6, pr * 0.85); calm = 0; lastDown = time; }
    else if (avg < 1 / 57) { calm++; if (calm >= 5 && pr < maxPR && time - lastDown > 12) { next = Math.min(maxPR, pr * 1.12); calm = 0; } }
    else calm = 0;
    if (Math.abs(next - pr) > 0.01) {
      pr = next;
      renderer.setPixelRatio(pr);
      resize();
    }
  }

  // ---- loop
  let last = performance.now();
  function loop(now) {
    const dt = Math.min(0.05, (now - last) / 1000);
    last = now;
    if (!state.paused && !params.has('freeze')) step(dt, rig.mode !== 'manual');
    ui.update(dt);
    applyDbgLook();
    post.grade.uniforms.uTime.value = time % 10;
    post.composer.render(dt);
    adapt(dt);
    window.__frames = (window.__frames || 0) + 1;
    requestAnimationFrame(loop);
  }
  requestAnimationFrame(loop);

  window.__ChipAudio = ChipAudio;
  window.__app = { THREE, scene, camera, renderer, sky, params, occ, spokes, SEED, village, vegetation, van, weasel, life, rig, post, state, step, audio, ui, get time() { return time; } };
}

boot();
